import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModeToggle } from '@/components/mode-toggle';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { useSidebar } from '@/hooks/useSidebar';
import {
  ReactFlow,
  Background,
  MarkerType,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatInternalName } from '../utils/formatInternalName';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2, Ban, CheckCircle2, XCircle, Timer,
  Clock, RefreshCw, ExternalLink, ShieldCheck, RotateCcw, ChevronDown, ShipWheel, EyeOff,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { parseFormattedText } from '../lib/textFormatting';
import { DiffView } from '../pages/shared/codeDiffUtils';
import { DatadogMetricsChart } from '../components/DatadogMetricsChart';
import { ExecutionDiagnosisChat } from '../components/ExecutionDiagnosisChat';
import {
  apiClient,
  type WorkflowExecutionDetail as WorkflowExecutionDetailPayload,
  type WorkflowNode as WFNode,
  type CustomBlock,
  type WorkflowApproval,
} from '../api/client';

import slackColorIcon from '../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import jiraIcon from '../assets/icons/jira.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import cloudflareIcon from '../assets/icons/cloudflare.svg';

type EmbeddedWorkflow = NonNullable<WorkflowExecutionDetailPayload['workflow']>;

// Trigger signal_source values that map 1:1 to a known integration icon.
// Anything else (or empty) falls back to the legacy Kubernetes default.
const KNOWN_TRIGGER_INTEGRATIONS = new Set([
  'pagerduty', 'posthog', 'vercel', 'railway', 'flyio', 'nebius', 'github', 'aws',
  'datadog', 'cloudflare', 'beam', 'daytona', 'supabase', 'planetscale', 'neon',
  'clickhouse', 'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'gcp', 'gcp-cost', 'gcp-security', 'gcp-data', 'terraform', 'pulumi', 'vault', 'infisical',
  'karpenter', 'kyverno', 'trivy', 'schedule',
]);

function useIsDark() {
  const { theme } = useTheme();
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

function getNodeIntegrationIcon(integration: string | undefined, nodeType: string | undefined, isDark: boolean, source?: string, customIconUrl?: string, customIconUrlDark?: string, signalFilter?: any): React.ReactNode {
  if (nodeType === 'trigger') {
    if (source === 'request') {
      const cat = (signalFilter?.request_categories || [])[0] || '';
      switch (cat) {
        case 'k8s': return <ShipWheel className="w-4 h-4 text-blue-500" />;
        case 'cloud': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />;
        case 'cloudflare': return <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />;
        case 'pagerduty': return <img src={pagerdutyColorIcon} alt="PagerDuty" className="w-4 h-4" />;
        case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />;
        case 'argocd': return <img src={argocdIcon} alt="ArgoCD" className="w-4 h-4" />;
        case 'argo-rollouts': return <img src={argocdIcon} alt="Argo Rollouts" className="w-4 h-4" />;
        case 'fluxcd': return <img src={fluxcdIcon} alt="Flux CD" className="w-4 h-4" />;
        case 'karpenter': return <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4" />;
        case 'kyverno': return <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4" />;
        case 'trivy': return <img src={trivyIcon} alt="Trivy" className="w-4 h-4" />;
        case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />;
        case 'gitlab': return <img src={gitlabIcon} alt="GitLab" className="w-4 h-4" />;
        case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" className="w-4 h-4" />;
        case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />;
        case 'railway': return <img src={railwayIcon} alt="Railway" className={`w-4 h-4 object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
        case 'flyio': return <img src={flyioIcon} alt="Fly.io" className={`w-4 h-4 object-contain`} />;
        case 'nebius': return <img src={nebiusIcon} alt="Nebius" className={`w-4 h-4 object-contain`} />;
        case 'beam': return <BeamIcon className={`w-4 h-4 text-foreground`} />;
        case 'daytona': return <DaytonaIcon className={`w-4 h-4 text-foreground`} />;
        case 'supabase': return <img src={supabaseIcon} alt="Supabase" className={`w-4 h-4 object-contain`} />;
        case 'planetscale': return <PlanetScaleIcon className={`w-4 h-4 text-foreground`} />;
        case 'neon': return <img src={neonIcon} alt="Neon" className={`w-4 h-4 object-contain`} />;
        case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" className={`w-4 h-4 object-contain`} />;
        case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" className={`w-4 h-4 object-contain`} />;
        case 'circleci': return <CircleCIIcon className={`w-4 h-4 text-foreground`} />;
        case 'sonarcloud': return <SonarCloudIcon className={`w-4 h-4 text-foreground`} />;
        case 'okta': return <OktaIcon className={`w-4 h-4 text-foreground`} />;
        case 'databricks': return <img src={databricksIcon} alt="Databricks" className={`w-4 h-4 object-contain`} />;
        case 'gcp': return <GCPIcon className="w-4 h-4" />;
        case 'terraform': return <img src={terraformIcon} alt="Terraform" className={`w-4 h-4 object-contain`} />;
        case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" className={`w-4 h-4 object-contain`} />;
        case 'vault': return <img src={vaultIcon} alt="Vault" className={`w-4 h-4 object-contain`} />;
        case 'infisical': return <img src={infisicalIcon} alt="Infisical" className={`w-4 h-4 object-contain rounded-sm`} />;
        default: return <img src={slackColorIcon} alt="Slack" className="w-4 h-4" />;
      }
    }
    if (source === 'pagerduty') return <img src={pagerdutyColorIcon} alt="PagerDuty" className="w-4 h-4" />;
    if (source === 'aws') return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />;
    if (source === 'posthog') return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-4 h-4 object-contain" />;
    if (source === 'vercel') return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />;
    if (source === 'railway') return <img src={railwayIcon} alt="Railway" className="w-4 h-4 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    if (source === 'flyio') return <img src={flyioIcon} alt="Fly.io" className="w-4 h-4 object-contain" />;
    if (source === 'nebius') return <img src={nebiusIcon} alt="Nebius" className="w-4 h-4 object-contain" />;
    if (source === 'beam') return <BeamIcon className="w-4 h-4 text-foreground" />;
    if (source === 'daytona') return <DaytonaIcon className="w-4 h-4 text-foreground" />;
    if (source === 'supabase') return <img src={supabaseIcon} alt="Supabase" className="w-4 h-4 object-contain" />;
    if (source === 'planetscale') return <PlanetScaleIcon className="w-4 h-4 text-foreground" />;
    if (source === 'neon') return <img src={neonIcon} alt="Neon" className="w-4 h-4 object-contain" />;
    if (source === 'clickhouse') return <img src={clickhouseIcon} alt="ClickHouse" className="w-4 h-4 object-contain" />;
    if (source === 'karpenter') return <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4 object-contain" />;
    if (source === 'kyverno') return <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4 object-contain" />;
    if (source === 'trivy') return <img src={trivyIcon} alt="Trivy" className="w-4 h-4 object-contain" />;
    if (source === 'jenkins') return <img src={jenkinsIcon} alt="Jenkins" className="w-4 h-4 object-contain" />;
    if (source === 'circleci') return <CircleCIIcon className="w-4 h-4 text-foreground" />;
    if (source === 'sonarcloud') return <SonarCloudIcon className="w-4 h-4 text-foreground" />;
    if (source === 'okta') return <OktaIcon className="w-4 h-4 text-foreground" />;
    if (source === 'databricks') return <img src={databricksIcon} alt="Databricks" className="w-4 h-4 object-contain" />;
    if (source === 'gcp') return <GCPIcon className="w-4 h-4" />;
    if (source === 'terraform') return <img src={terraformIcon} alt="Terraform" className="w-4 h-4 object-contain" />;
    if (source === 'pulumi') return <img src={pulumiIcon} alt="Pulumi" className="w-4 h-4 object-contain" />;
    if (source === 'vault') return <img src={vaultIcon} alt="Vault" className="w-4 h-4 object-contain" />;
    if (source === 'infisical') return <img src={infisicalIcon} alt="Infisical" className="w-4 h-4 object-contain rounded-sm" />;
    if (source === 'github') return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />;
    if (source === 'cloudflare') return <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />;
    if (source === 'datadog') return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />;
    if (source === 'schedule') return <Clock className="w-4 h-4 text-zinc-900 dark:text-zinc-100" />;
    if (source === 'custom_webhook') {
      const icon = isDark ? (customIconUrlDark || customIconUrl) : (customIconUrl || customIconUrlDark);
      if (icon) return <img src={icon} alt="Custom" className="w-4 h-4 object-contain" />;
      return null;
    }
    return <img src="https://raw.githubusercontent.com/kubernetes/kubernetes/master/logo/logo.svg" alt="K8s" className="w-4 h-4" />;
  }
  if (nodeType === 'approval') {
    return <ShieldCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />;
  }
  switch (integration) {
    case 'kestrel':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className="w-4 h-4" />;
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />;
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" className="w-4 h-4" />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" className="w-4 h-4" />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" className="w-4 h-4" />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" className="w-4 h-4" />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className="w-4 h-4" />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" className="w-4 h-4" />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />;
    case 'argocd':
      return <img src={argocdIcon} alt="ArgoCD" className="w-4 h-4" />;
    case 'argo-rollouts':
      return <img src={argocdIcon} alt="Argo Rollouts" className="w-4 h-4" />;
    case 'fluxcd':
      return <img src={fluxcdIcon} alt="Flux CD" className="w-4 h-4" />;
    case 'karpenter':
      return <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4" />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4" />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" className="w-4 h-4" />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-4 h-4 object-contain" />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" className={`w-4 h-4 object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" className={`w-4 h-4 object-contain`} />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" className={`w-4 h-4 object-contain`} />;
    case 'beam':
      return <BeamIcon className={`w-4 h-4 text-foreground`} />;
    case 'daytona':
      return <DaytonaIcon className={`w-4 h-4 text-foreground`} />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" className={`w-4 h-4 object-contain`} />;
    case 'planetscale':
      return <PlanetScaleIcon className={`w-4 h-4 text-foreground`} />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" className={`w-4 h-4 object-contain`} />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" className={`w-4 h-4 object-contain`} />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" className={`w-4 h-4 object-contain`} />;
    case 'circleci':
      return <CircleCIIcon className={`w-4 h-4 text-foreground`} />;
    case 'sonarcloud':
      return <SonarCloudIcon className={`w-4 h-4 text-foreground`} />;
    case 'okta':
      return <OktaIcon className={`w-4 h-4 text-foreground`} />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" className={`w-4 h-4 object-contain`} />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" className={`w-4 h-4 object-contain`} />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" className={`w-4 h-4 object-contain`} />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" className={`w-4 h-4 object-contain`} />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" className={`w-4 h-4 object-contain rounded-sm`} />;
    case 'cloudflare':
      return <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />;
    case 'kubernetes':
      return <ShipWheel className="w-4 h-4 text-blue-500" />;
    case 'gcp':
    case 'gcp-cost':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPIcon className="w-4 h-4" />;
    case 'custom': {
      const icon = isDark ? (customIconUrlDark || customIconUrl) : (customIconUrl || customIconUrlDark);
      if (icon) return <img src={icon} alt="Custom" className="w-4 h-4 object-contain" />;
      return null;
    }
    default:
      return null;
  }
}

// Execution-aware node — wraps the workflow node data with a coloured status border.
function ExecutionNode({ data }: any) {
  const isDark = useIsDark();
  const statusStyles: Record<string, string> = {
    completed: 'border-green-500 bg-green-50 dark:bg-green-950/40',
    running: 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 ring-4 ring-blue-500/30 shadow-lg shadow-blue-500/20',
    failed: 'border-red-500 bg-red-50 dark:bg-red-950/40',
    waiting_approval: 'border-amber-500 bg-amber-50 dark:bg-amber-950/40 ring-4 ring-amber-500/30',
    waiting: 'border-amber-500 bg-amber-50 dark:bg-amber-950/40 ring-4 ring-amber-500/30',
    skipped: 'border-gray-300 bg-gray-50 dark:bg-gray-900 opacity-50',
    pending: 'border-border bg-muted/30',
  };

  const statusIcons: Record<string, React.ReactNode> = {
    completed: <CheckCircle2 className="w-4 h-4 text-green-600" />,
    running: <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />,
    failed: <XCircle className="w-4 h-4 text-red-600" />,
    waiting_approval: <Timer className="w-4 h-4 text-amber-600" />,
    waiting: <Timer className="w-4 h-4 text-amber-600" />,
    skipped: <Ban className="w-4 h-4 text-muted-foreground" />,
    pending: <Clock className="w-4 h-4 text-muted-foreground" />,
  };

  const isTrigger = data._nodeType === 'trigger';
  const stepStatus = isTrigger ? 'completed' : (data._stepStatus || 'pending');
  const style = statusStyles[stepStatus] || statusStyles.pending;
  const icon = statusIcons[stepStatus] || statusIcons.pending;

  return (
    <div className="relative">
      {data._nodeType !== 'trigger' && (
        <Handle type="target" position={Position.Left} className="!bg-transparent !border-none !w-0 !h-0 !min-w-0 !min-h-0" />
      )}
      <Handle type="source" position={Position.Right} className="!bg-transparent !border-none !w-0 !h-0 !min-w-0 !min-h-0" />
      <Card className={`w-[260px] p-3 border-2 transition-all ${style}`}>
        {(stepStatus === 'running' || stepStatus === 'waiting_approval' || stepStatus === 'waiting') && (
          <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-1.5">
            {stepStatus === 'running' ? '● Current Step' : stepStatus === 'waiting' ? '● Polling' : '● Awaiting Approval'}
          </div>
        )}
        <div className="flex items-center gap-2 mb-1">
          <div className="flex flex-col items-center gap-1 shrink-0">
            {getNodeIntegrationIcon(data.integration, data._nodeType, isDark, data.source, data.custom_icon_url, data.custom_icon_url_dark, data.signal_filter)}
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {data._nodeType || 'step'}
              {data.integration && ` · ${data.integration === 'custom' && data.integration_name ? data.integration_name : data.integration}`}
            </div>
            <div className="text-sm font-semibold truncate">{data.label || data.action || 'Step'}</div>
          </div>
          {stepStatus === 'completed' && data._stepDuration && (
            <span className="text-[10px] text-muted-foreground shrink-0">{data._stepDuration}</span>
          )}
          {typeof data._stepIteration === 'number' && (
            <span className="text-[10px] text-muted-foreground shrink-0">iter {data._stepIteration}</span>
          )}
        </div>
        {data._stepError && (
          <div className="text-xs text-destructive mt-1 truncate" title={data._stepError}>
            {data._stepError}
          </div>
        )}
      </Card>
    </div>
  );
}

const nodeTypes = { execution: ExecutionNode };

const WorkflowExecutionDetail: React.FC = () => {
  const { id: workflowId, execId } = useParams<{ id: string; execId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isDark = useIsDark();
  const { isMobile } = useSidebar();
  const [workflow, setWorkflow] = useState<EmbeddedWorkflow | null>(null);
  const [execution, setExecution] = useState<WorkflowExecutionDetailPayload | null>(null);
  const [clusterMap, setClusterMap] = useState<Record<string, string>>({});
  const [customBlocks, setCustomBlocks] = useState<CustomBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<any | null>(null);

  const [panelWidth, setPanelWidth] = useState(420);
  const [pendingApprovals, setPendingApprovals] = useState<WorkflowApproval[]>([]);
  const [approvalFeedback, setApprovalFeedback] = useState<Record<string, string>>({});
  const [approvalProcessingIds, setApprovalProcessingIds] = useState<Set<string>>(new Set());
  const [approvalActionMessage, setApprovalActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [diagnosisChatVisible, setDiagnosisChatVisible] = useState(false);
  const [diagChatWidth, setDiagChatWidth] = useState(() => Math.round(window.innerWidth * 0.28));
  const isDiagResizing = useRef(false);
  const diagResizeLineRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const resizeLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing.current && !isDiagResizing.current) return;
      if (isResizing.current) {
        const newWidth = Math.max(300, Math.min(700, window.innerWidth - e.clientX));
        setPanelWidth(newWidth);
      }
      if (isDiagResizing.current) {
        const newWidth = Math.max(Math.round(window.innerWidth * 0.23), Math.min(Math.round(window.innerWidth * 0.45), window.innerWidth - e.clientX));
        setDiagChatWidth(newWidth);
      }
    };
    const onMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizeLineRef.current) {
          resizeLineRef.current.style.transitionDelay = '0ms';
          resizeLineRef.current.style.backgroundColor = 'transparent';
        }
      }
      if (isDiagResizing.current) {
        isDiagResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (diagResizeLineRef.current) {
          diagResizeLineRef.current.style.transitionDelay = '0ms';
          diagResizeLineRef.current.style.backgroundColor = 'transparent';
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizeLineRef.current) {
      resizeLineRef.current.style.transitionDelay = '0ms';
      resizeLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user && workflowId && execId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, workflowId, execId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // The execution endpoint embeds a snapshot of the parent workflow via
      // LEFT JOIN, so this works even when the parent has been soft-deleted.
      // Do NOT also call apiClient.getWorkflow(workflowId): that endpoint
      // filters deleted_at IS NULL and 404s on deleted parents.
      const exec = await apiClient.getWorkflowExecution(execId!);
      setExecution(exec);
      setWorkflow(exec.workflow ?? null);

      try {
        const clustersResp = await apiClient.listClusters();
        const map: Record<string, string> = {};
        for (const c of clustersResp.clusters || []) {
          map[c.id] = c.cluster_name;
        }
        setClusterMap(map);
      } catch { /* clusters not critical */ }

      try {
        const blocks = await apiClient.listCustomBlocks();
        setCustomBlocks(blocks);
      } catch { /* custom blocks not critical */ }

      await refreshPendingApprovals();
    } catch (err: any) {
      setError(err?.message || 'Failed to load execution');
    } finally {
      setLoading(false);
    }
  };

  const refreshPendingApprovals = useCallback(async () => {
    if (!execId) return;
    try {
      const pending = await apiClient.listPendingApprovals();
      setPendingApprovals(pending.filter((a) => a.execution_id === execId));
    } catch {
      // approvals are non-critical for rendering the execution
    }
  }, [execId]);

  const handleApprovalAction = useCallback(
    async (approval: WorkflowApproval, action: 'approve' | 'reject' | 'request-changes') => {
      const id = approval.id;
      const feedback = (approvalFeedback[id] || '').trim();
      if (action === 'request-changes' && !feedback) {
        setApprovalActionMessage({ type: 'error', text: 'Describe the changes you want before requesting changes.' });
        setTimeout(() => setApprovalActionMessage(null), 4000);
        return;
      }
      setApprovalProcessingIds((prev) => new Set(prev).add(id));
      try {
        if (action === 'approve') {
          await apiClient.approveWorkflowStep(id, feedback || undefined);
          setApprovalActionMessage({ type: 'success', text: 'Approval granted — the workflow will resume.' });
        } else if (action === 'reject') {
          await apiClient.rejectWorkflowStep(id, feedback || undefined);
          setApprovalActionMessage({ type: 'success', text: 'Approval rejected — the workflow will stop.' });
        } else {
          await apiClient.requestChangesWorkflowStep(id, feedback);
          setApprovalActionMessage({ type: 'success', text: 'Changes requested — the AI is re-running with your guidance.' });
        }
        setApprovalFeedback((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setTimeout(() => setApprovalActionMessage(null), 6000);
        // Refresh execution + approvals to reflect the resolved/re-opened gate.
        await handleRefresh();
        setTimeout(() => { refreshPendingApprovals(); }, 4000);
      } catch (err: any) {
        setApprovalActionMessage({ type: 'error', text: err?.response?.data?.error || err?.message || 'Action failed.' });
        setTimeout(() => setApprovalActionMessage(null), 6000);
      } finally {
        setApprovalProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    // handleRefresh is declared below; it's stable via useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approvalFeedback, refreshPendingApprovals],
  );

  const handleRefresh = useCallback(async () => {
    if (!execId) return;
    try {
      const exec = await apiClient.getWorkflowExecution(execId);
      setExecution(exec);
      // Keep the embedded workflow snapshot fresh too (status / deleted_at
      // can change while the page is open).
      setWorkflow(exec.workflow ?? null);
    } catch {
      // ignore refresh errors
    }
    refreshPendingApprovals();
  }, [execId]);

  // Poll for updates every 10s.
  useEffect(() => {
    if (!execution) return;
    const interval = setInterval(handleRefresh, 10000);
    return () => clearInterval(interval);
  }, [execution, handleRefresh]);

  const handleCancel = async () => {
    if (!execution) return;
    try {
      await apiClient.cancelWorkflowExecution(execution.id);
      await handleRefresh();
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel');
    }
  };

  const [replayLoading, setReplayLoading] = useState(false);
  const handleReplay = async (mode: 'full' | 'from_failed') => {
    if (!execution) return;
    setReplayLoading(true);
    try {
      const result = await apiClient.replayWorkflowExecution(execution.id, mode);
      navigate(`/workflows/${execution.workflow_id}/executions/${result.execution_id}`);
    } catch (err: any) {
      setError(err?.response?.data || err?.message || 'Failed to replay');
    } finally {
      setReplayLoading(false);
    }
  };

  // Build the execution graph with status overlays.
  const { graphNodes, graphEdges } = useMemo(() => {
    if (!workflow?.definition || !execution) return { graphNodes: [], graphEdges: [] };

    const stepResults: Record<string, any> = {};
    if (Array.isArray(execution.step_results)) {
      for (const sr of execution.step_results) {
        if (Array.isArray(sr)) {
          for (const inner of sr) {
            if (inner?.step_id) stepResults[inner.step_id] = inner;
          }
        } else if (sr?.step_id) {
          stepResults[sr.step_id] = sr;
        }
      }
    }

    const blockMap = new Map(customBlocks.map(b => [`custom-${b.id}`, b]));

    const nodes: Node[] = workflow.definition.nodes.map((n: WFNode) => {
      const sr = stepResults[n.id];
      let stepStatus = 'pending';
      let stepError = '';
      let stepDuration = '';
      let stepIteration: number | undefined;

      if (sr) {
        stepStatus = sr.status || 'completed';
        stepError = sr.error || '';
        if (sr.started_at && sr.completed_at) {
          const ms = new Date(sr.completed_at).getTime() - new Date(sr.started_at).getTime();
          stepDuration = ms < 1000 ? '<1s' : `${Math.round(ms / 1000)}s`;
        }
        if (n.type === 'loop' && typeof sr.output?.iteration === 'number') {
          stepIteration = sr.output.iteration;
        }
        if (n.type === 'for_each' && typeof sr.output?.items_processed === 'number') {
          stepIteration = sr.output.items_processed;
        }
      } else if (execution.current_step_id === n.id) {
        stepStatus = execution.status === 'waiting_approval' ? 'waiting_approval' : execution.status === 'waiting' ? 'running' : 'running';
      }

      let enrichedData = { ...n.data };
      if (n.data?.integration === 'custom') {
        const cb = blockMap.get(n.data.action);
        if (cb) {
          const groupIcon = customBlocks.find(b => b.integration_name === cb.integration_name && b.icon_url)?.icon_url || '';
          const groupIconDark = customBlocks.find(b => b.integration_name === cb.integration_name && b.icon_url_dark)?.icon_url_dark || '';
          enrichedData = {
            ...enrichedData,
            custom_icon_url: enrichedData.custom_icon_url || cb.icon_url || groupIcon || '',
            custom_icon_url_dark: enrichedData.custom_icon_url_dark || cb.icon_url_dark || groupIconDark || '',
            custom_color: enrichedData.custom_color || cb.color || '',
            integration_name: enrichedData.integration_name || cb.integration_name || '',
          };
        }
      }
      if (n.data?.source === 'custom_webhook') {
        let cb: typeof customBlocks[0] | undefined;
        if (n.data.custom_block_id) cb = customBlocks.find(b => b.id === n.data.custom_block_id);
        if (!cb && n.data.template_id) {
          const tid = (n.data.template_id as string).replace('custom-trigger-', '');
          cb = customBlocks.find(b => b.id === tid);
        }
        if (!cb) cb = customBlocks.find(b => b.block_type === 'trigger');
        if (cb) {
          const m = cb;
          const groupIcon = customBlocks.find(b => b.integration_name === m.integration_name && b.icon_url)?.icon_url || '';
          const groupIconDark = customBlocks.find(b => b.integration_name === m.integration_name && b.icon_url_dark)?.icon_url_dark || '';
          enrichedData = {
            ...enrichedData,
            custom_icon_url: enrichedData.custom_icon_url || m.icon_url || groupIcon || '',
            custom_icon_url_dark: enrichedData.custom_icon_url_dark || m.icon_url_dark || groupIconDark || '',
            custom_color: enrichedData.custom_color || m.color || '',
            integration_name: enrichedData.integration_name || m.integration_name || '',
          };
        }
      }

      return {
        id: n.id,
        type: 'execution',
        position: n.position,
        data: {
          ...enrichedData,
          _nodeType: n.type,
          _stepStatus: stepStatus,
          _stepError: stepError,
          _stepDuration: stepDuration,
          _stepIteration: stepIteration,
        },
      };
    });

    const edges: Edge[] = workflow.definition.edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'default',
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
      style: { strokeWidth: 2, stroke: '#94a3b8' },
    }));

    return { graphNodes: nodes, graphEdges: edges };
  }, [workflow, execution, customBlocks]);

  const handleNodeClick = useCallback((_: any, node: Node) => {
    if (!execution) return;

    // Handle trigger node — show signal details
    if (node.data?._nodeType === 'trigger') {
      const signal = execution.trigger_signal || {};
      const signalSource = signal.signal_source || signal.metadata?.signal_source || '';
      const clusterId = signal.cluster_id || signal.metadata?.cluster_id || '';
      const clusterName = signal.cluster_name || signal.metadata?.cluster_name || clusterMap[clusterId] || clusterId;
      setSelectedStep({
        step_id: node.id,
        node_type: 'trigger',
        status: 'completed',
        action: 'signal-trigger',
        integration: signalSource === 'request' ? 'slack' : (KNOWN_TRIGGER_INTEGRATIONS.has(signalSource) ? signalSource : 'kubernetes'),
        output: {
          _trigger: true,
          signal_source: signalSource,
          signal_type: signal.signal_type,
          reason: signal.reason,
          resource_name: signal.resource_name,
          resource_kind: signal.resource_kind,
          namespace: signal.namespace,
          severity: signal.severity,
          message: signal.message,
          cluster_id: clusterId,
          cluster_name: clusterName,
          requester: signal.requester || signal.metadata?.requester || '',
          requester_name: signal.requester_name || signal.metadata?.requester || '',
          event_type: signal.metadata?.event_type || signal.reason || '',
          session_id: signal.metadata?.session_id || '',
          distinct_id: signal.metadata?.distinct_id || '',
          replay_url: signal.metadata?.replay_url || '',
          // Log alert fields
          logs_summary: signal.metadata?.logs_summary || '',
          log_message: signal.metadata?.log_message || '',
          log_count: signal.metadata?.log_count || 0,
          service_name: signal.metadata?.service_name || '',
          // Preserve full metadata for fallback access
          ...signal.metadata,
        },
        started_at: execution.started_at,
        completed_at: execution.started_at,
      });
      return;
    }

    const flatResults = Array.isArray(execution.step_results)
      ? execution.step_results.flatMap((s: any) => Array.isArray(s) ? s : [s])
      : [];
    const sr = flatResults.find((s: any) => s.step_id === node.id);
    if (sr && sr.integration === 'custom' && customBlocks.length > 0) {
      const cb = customBlocks.find(b => `custom-${b.id}` === sr.action);
      if (cb) {
        sr._integration_name = cb.integration_name || '';
      }
    }
    setSelectedStep(sr || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execution, clusterMap, customBlocks]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workflow || !execution) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Execution not found.</AlertDescription>
      </Alert>
    );
  }

  const isLive = execution.status === 'running' || execution.status === 'waiting_approval' || execution.status === 'waiting';

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] -mx-4 sm:-mx-6 lg:-mx-8 -mt-4">
      {/* Page header */}
      <div className="bg-background shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex items-start gap-1">
              <MobileMenuButton />
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-foreground truncate">{workflow.name}</h1>
                  <ExecutionStatusBadge status={execution.status} />
                  {(execution.trigger_signal?.test_execution || execution.trigger_signal?.metadata?.test_execution) && (
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600 dark:text-amber-400">Test</Badge>
                  )}
                  {(execution.trigger_signal?.simulated || execution.trigger_signal?.metadata?.simulated) && (
                    <Badge
                      variant="outline"
                      className="text-xs border-amber-500/50 text-amber-600 dark:text-amber-400"
                      title="Simulated execution: no real integration actions were performed; step outputs are mocked."
                    >
                      Simulated
                    </Badge>
                  )}
                  {workflow.deleted_at && (
                    <Badge variant="outline" className="text-xs border-muted-foreground/50 text-muted-foreground" title={`Workflow deleted at ${new Date(workflow.deleted_at).toLocaleString()}`}>
                      Workflow deleted
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  Execution {execution.id.slice(0, 8)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 shrink-0">
              {isLive && (
                <>
                  <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-1">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCancel} className="gap-1 text-destructive">
                    <Ban className="w-3 h-3" /> Cancel
                  </Button>
                </>
              )}
              {execution?.status === 'failed' && !diagnosisChatVisible && (
                <Button variant="outline" size="sm" onClick={() => setDiagnosisChatVisible(true)}>
                  Open Chat
                </Button>
              )}
              {execution?.status === 'failed' && (
                <ReplayDropdown loading={replayLoading} onReplay={handleReplay} />
              )}
              <ModeToggle />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="m-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Graph + detail panel + Diagnosis Chat */}
      <div className="flex flex-1 overflow-hidden">
        {/* Graph area */}
        <div className="flex-1 min-w-0 h-full relative">
          <ReactFlow
            nodes={graphNodes}
            edges={graphEdges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1.1 }}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            className="bg-muted/20"
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
          </ReactFlow>
        </div>

        {/* Step detail panel */}
        {selectedStep && (
          <>
          <div
            onMouseDown={startResize}
            onMouseEnter={() => {
              if (resizeLineRef.current) {
                resizeLineRef.current.style.transitionDelay = '400ms';
                resizeLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.3)';
              }
            }}
            onMouseLeave={() => {
              if (resizeLineRef.current && !isResizing.current) {
                resizeLineRef.current.style.transitionDelay = '0ms';
                resizeLineRef.current.style.backgroundColor = 'transparent';
              }
            }}
            className={isMobile ? 'hidden' : 'shrink-0 cursor-col-resize z-20 flex items-stretch justify-center'}
            style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
          >
            <div
              ref={resizeLineRef}
              className="w-[3px] rounded-full bg-transparent transition-colors"
              style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
            />
          </div>
          <div
            className={isMobile
              ? 'fixed inset-0 z-50 bg-background flex flex-col'
              : 'border-l bg-background flex flex-col shrink-0'}
            style={isMobile ? undefined : { width: panelWidth }}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-sm">Step Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedStep(null)} className="h-7 w-7 p-0">
                ×
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-3 text-sm">
                {/* Trigger node: show signal details */}
                {selectedStep.output?._trigger ? (() => {
                  const signalSource = selectedStep.output?.signal_source || selectedStep.output?.metadata?.signal_source || '';
                  const signalType = selectedStep.output?.signal_type || '';
                  const isDark = document.documentElement.classList.contains('dark');
                  const isRequest = signalSource === 'request' || signalType === 'request';
                  const isCloud = !isRequest && (signalSource === 'aws' ||
                    signalType.includes('cloudtrail') ||
                    signalType.includes('cloudwatch'));
                  const isPagerDuty = signalSource === 'pagerduty' || signalType.startsWith('incident.');
                  const isPostHog = signalSource === 'posthog';
                  // Human-readable label per trigger source; anything unknown
                  // falls back to the legacy Kubernetes default (matches the
                  // icon fallback in getNodeIntegrationIcon).
                  const SIGNAL_SOURCE_LABELS: Record<string, string> = {
                    posthog: 'PostHog Signal',
                    vercel: 'Vercel Signal',
                    railway: 'Railway Signal',
                    flyio: 'Fly.io Signal',
                    nebius: 'Nebius Signal',
                    beam: 'Beam Signal',
                    daytona: 'Daytona Signal',
                    supabase: 'Supabase Signal',
                    planetscale: 'PlanetScale Signal',
                    neon: 'Neon Signal',
                    clickhouse: 'ClickHouse Signal',
                    jenkins: 'Jenkins Signal',
                    circleci: 'CircleCI Signal',
                    sonarcloud: 'SonarCloud Signal',
                    okta: 'Okta Signal',
                    databricks: 'Databricks Signal',
                    terraform: 'Terraform Signal',
                    pulumi: 'Pulumi Signal',
                    vault: 'Vault Signal',
                    infisical: 'Infisical Signal',
                    github: 'GitHub Signal',
                    datadog: 'Datadog Signal',
                    cloudflare: 'Cloudflare Signal',
                    karpenter: 'Karpenter Signal',
                    kyverno: 'Kyverno Signal',
                    trivy: 'Trivy Signal',
                    schedule: 'Scheduled Trigger',
                    custom_webhook: 'Custom Webhook',
                  };
                  const signalLabel = isRequest ? 'Slack Request'
                    : isPagerDuty ? 'PagerDuty Signal'
                    : isCloud ? 'Cloud Signal'
                    : SIGNAL_SOURCE_LABELS[signalSource] || 'Kubernetes Signal';
                  const signalColor = 'text-foreground';

                  if (isRequest) {
                    const requester = selectedStep.output.requester || selectedStep.output.requester_name || '';
                    const category = selectedStep.output.reason || selectedStep.output.category || '';
                    return (
                    <>
                      <div>
                        <span className={`font-medium ${signalColor}`}>{signalLabel}</span>
                      </div>
                      {requester && (
                        <div>
                          <span className="text-muted-foreground">Requester:</span>{' '}
                          <span className="font-semibold">{requester}</span>
                        </div>
                      )}
                      {category && (
                        <div>
                          <span className="text-muted-foreground">Category:</span>{' '}
                          <span className="font-medium">{formatInternalName(category)}</span>
                        </div>
                      )}
                      {selectedStep.output.message && (
                        <div>
                          <span className="text-muted-foreground">Message:</span>
                          <div className="mt-1 text-xs bg-muted p-2 rounded">{selectedStep.output.message}</div>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Detected:</span>{' '}
                        {new Date(selectedStep.started_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                      </div>
                    </>
                    );
                  }

                  if (isPostHog) {
                    const eventType = selectedStep.output.event_type || selectedStep.output.reason || '';
                    const sessionId = selectedStep.output.session_id || '';
                    const distinctId = selectedStep.output.distinct_id || '';
                    const replayUrl = selectedStep.output.replay_url || '';
                    return (
                    <>
                      <div>
                        <span className={`font-medium ${signalColor}`}>{signalLabel}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Event Type:</span>{' '}
                        <span className="font-semibold">{eventType}</span>
                      </div>
                      {sessionId && (
                        <div>
                          <span className="text-muted-foreground">Session ID:</span>{' '}
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{sessionId}</code>
                        </div>
                      )}
                      {distinctId && (
                        <div>
                          <span className="text-muted-foreground">Distinct ID:</span>{' '}
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{distinctId}</code>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Detected:</span>{' '}
                        {new Date(selectedStep.started_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                      </div>
                      {replayUrl && (
                        <div className="mt-2">
                          <a
                            href={replayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                          >
                            <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-3.5 h-3.5 object-contain" />
                            <span>View Session Replay</span>
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        </div>
                      )}
                      {eventType === 'log_alert' && (() => {
                        const props = selectedStep.output.properties || {};
                        const logsSummary = selectedStep.output.logs_summary || props.logs_summary || selectedStep.output.log_message || '';
                        const logCount = selectedStep.output.log_count || props.log_count || 0;
                        const svcName = selectedStep.output.service_name || props.service_name || '';
                        return (
                          <>
                            {logCount > 0 && (
                              <div>
                                <span className="text-muted-foreground">Error Count:</span>{' '}
                                <span className="font-semibold text-red-500">{logCount}</span>
                              </div>
                            )}
                            {svcName && (
                              <div>
                                <span className="text-muted-foreground">Service:</span>{' '}
                                <span className="font-semibold">{svcName}</span>
                              </div>
                            )}
                            {logsSummary && (
                              <div className="mt-2">
                                <span className="text-muted-foreground text-xs font-medium">Error Logs:</span>
                                <pre className="mt-1 text-xs bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200 rounded p-2 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-all border border-red-200 dark:border-red-900/30">{logsSummary}</pre>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                    );
                  }

                  return (
                  <>
                    <div>
                      <span className={`font-medium ${signalColor}`}>{signalLabel}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Reason:</span>{' '}
                      <span className="font-semibold">{selectedStep.output.reason}</span>
                    </div>
                    {(selectedStep.output.namespace || selectedStep.output.resource_name) && (
                    <div>
                      <span className="text-muted-foreground">Resource:</span>{' '}
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{selectedStep.output.namespace ? `${selectedStep.output.namespace}/` : ''}{selectedStep.output.resource_name}</code>
                    </div>
                    )}
                    {selectedStep.output.resource_kind && (
                    <div>
                      <span className="text-muted-foreground">Kind:</span>{' '}
                      <span className="font-medium">{selectedStep.output.resource_kind}</span>
                    </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Severity:</span>{' '}
                      <span className="font-medium">{selectedStep.output.severity}</span>
                    </div>
                    {(selectedStep.output.cluster_name || selectedStep.output.cluster_id) && (
                      <div>
                        <span className="text-muted-foreground">Cluster:</span>{' '}
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{selectedStep.output.cluster_name || selectedStep.output.cluster_id}</code>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Signal Type:</span>{' '}
                      <span className="font-medium">{formatInternalName(selectedStep.output.signal_type)}</span>
                    </div>
                    {selectedStep.output.message && (
                      <div>
                        <span className="text-muted-foreground">Message:</span>
                        <div className="mt-1 text-xs bg-muted p-2 rounded">{selectedStep.output.message}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Detected:</span>{' '}
                      {new Date(selectedStep.started_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                    </div>
                  </>
                  );
                })() : (
                  <>
                <div>
                  <span className="text-muted-foreground">Step ID:</span>{' '}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">{selectedStep.step_id}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <span className="font-medium">{selectedStep.status}</span>
                </div>
                {selectedStep.integration && (
                  <div>
                    <span className="text-muted-foreground">Integration:</span>{' '}
                    <span className="font-medium">{selectedStep.integration === 'custom' && selectedStep._integration_name ? selectedStep._integration_name : selectedStep.integration}</span>
                  </div>
                )}
                {selectedStep.action && (
                  <div>
                    <span className="text-muted-foreground">Action:</span>{' '}
                    <span className="font-medium">{selectedStep.action}</span>
                  </div>
                )}
                {selectedStep.error && (
                  <div>
                    <span className="text-muted-foreground">Error:</span>
                    <div className="mt-1 text-xs text-destructive bg-destructive/10 p-2 rounded">
                      {selectedStep.error}
                    </div>
                  </div>
                )}
                {/* Rich RCA display for kestrel-trigger-rca */}
                {selectedStep.output?.rca_result && (
                  <div className="space-y-3">
                    {selectedStep.output.rca_result.root_cause && (
                      <div>
                        <span className="font-medium">Root Cause</span>
                        <div className="mt-1 text-xs bg-muted p-2 rounded leading-relaxed">
                          {parseFormattedText(selectedStep.output.rca_result.root_cause)}
                        </div>
                      </div>
                    )}
                    {selectedStep.output.rca_result.incident_type && (
                      <div>
                        <span className="text-muted-foreground">Type:</span>{' '}
                        <span className="font-medium">{selectedStep.output.rca_result.incident_type}</span>
                        {selectedStep.output.rca_result.severity && (
                          <> · <span className="font-medium">{selectedStep.output.rca_result.severity}</span></>
                        )}
                      </div>
                    )}
                    {workflowId && execId && selectedStep.step_id && (
                      <button
                        type="button"
                        onClick={() => navigate(`/workflows/${workflowId}/executions/${execId}/rca/${selectedStep.step_id}`)}
                        className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                      >
                        <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className="w-4 h-4" />
                        <span>View Incident Details</span>
                      </button>
                    )}
                    {selectedStep.output.rca_result.is_application_level_failure && (
                      <div className="text-xs bg-muted/50 text-foreground border border-border p-2 rounded">
                        Application code issue detected
                      </div>
                    )}
                    {selectedStep.output.rca_result.stack_trace_summary && (
                      <div>
                        <span className="font-medium">Stack Trace</span>
                        <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-40">
                          {selectedStep.output.rca_result.stack_trace_summary}
                        </pre>
                      </div>
                    )}
                    {/* Show fixes from RCA output */}
                    {selectedStep.output.fixes && Array.isArray(selectedStep.output.fixes) && selectedStep.output.fixes.length > 0 && (
                      <div>
                        <span className="font-medium">{selectedStep.output.fixes.length} Fix(es)</span>
                        <div className="mt-1 space-y-3">
                          {selectedStep.output.fixes.map((fix: any, i: number) => (
                            <YAMLFixCard key={i} fix={fix} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Generate Fix step: show fixes from the RCA step's output */}
                {selectedStep.action === 'kestrel-trigger-fix-generation' && !selectedStep.output?.rca_result && (
                  <FixesFromRCAStep stepResults={execution?.step_results} />
                )}
                {/* Datadog metrics visualization */}
                {selectedStep.action === 'datadog-query-metrics' && selectedStep.output?.response_data && (
                  <DatadogMetricsFromStep output={selectedStep.output} />
                )}
                {/* Generic output for non-RCA / non-fix / non-metrics steps */}
                {selectedStep.output && !selectedStep.output.rca_result && selectedStep.action !== 'kestrel-trigger-fix-generation' && selectedStep.action !== 'datadog-query-metrics' && Object.keys(selectedStep.output).length > 0 && (
                  <div>
                    <span className="text-muted-foreground">Output:</span>
                    {Object.values(selectedStep.output).some((v: any) => v === '[REDACTED]') && (
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 border border-border rounded px-2 py-1.5">
                        <EyeOff className="w-3.5 h-3.5 shrink-0" />
                        <span>Sensitive outputs (secret values) are redacted here but were available to downstream steps during execution.</span>
                      </div>
                    )}
                    <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto overflow-y-auto whitespace-pre-wrap max-h-[400px]">
                      {JSON.stringify(selectedStep.output, null, 2)}
                    </pre>
                  </div>
                )}
                {/* Inline approval actions: resolve the gate without leaving this page. */}
                {(() => {
                  const approval = pendingApprovals.find((a) => a.step_id === selectedStep.step_id);
                  if (!approval) return null;
                  return (
                    <StepApprovalActions
                      approval={approval}
                      feedback={approvalFeedback[approval.id] || ''}
                      onFeedbackChange={(val) => setApprovalFeedback((prev) => ({ ...prev, [approval.id]: val }))}
                      isProcessing={approvalProcessingIds.has(approval.id)}
                      message={approvalActionMessage}
                      onApprove={() => handleApprovalAction(approval, 'approve')}
                      onReject={() => handleApprovalAction(approval, 'reject')}
                      onRequestChanges={() => handleApprovalAction(approval, 'request-changes')}
                    />
                  );
                })()}
                {selectedStep.started_at && (
                  <div>
                    <span className="text-muted-foreground">Started:</span>{' '}
                    {new Date(selectedStep.started_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                  </div>
                )}
                {selectedStep.completed_at && (
                  <div>
                    <span className="text-muted-foreground">Completed:</span>{' '}
                    {new Date(selectedStep.completed_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                  </div>
                )}
                <ExternalStepLinks step={selectedStep} />
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
          </>
        )}

        {/* Diagnosis Chat Sidebar */}
        {diagnosisChatVisible && execution && workflow && (
          <>
          <div
            onMouseDown={(e) => { e.preventDefault(); isDiagResizing.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; if (diagResizeLineRef.current) { diagResizeLineRef.current.style.transitionDelay = '0ms'; diagResizeLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)'; } }}
            onMouseEnter={() => { if (!isDiagResizing.current && diagResizeLineRef.current) { diagResizeLineRef.current.style.transitionDelay = '300ms'; diagResizeLineRef.current.style.backgroundColor = 'hsl(var(--border))'; } }}
            onMouseLeave={() => { if (!isDiagResizing.current && diagResizeLineRef.current) { diagResizeLineRef.current.style.transitionDelay = '0ms'; diagResizeLineRef.current.style.backgroundColor = 'transparent'; } }}
            className={isMobile ? 'hidden' : 'shrink-0 cursor-col-resize z-20 flex items-stretch justify-center'}
            style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
          >
            <div
              ref={diagResizeLineRef}
              className="w-[3px] rounded-full bg-transparent transition-colors"
              style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
            />
          </div>
          <div
            className={isMobile
              ? 'fixed inset-0 z-50 bg-background flex flex-col overflow-hidden'
              : 'min-w-0 flex flex-col border-l border-border overflow-hidden'}
            style={isMobile ? undefined : { width: diagChatWidth }}
          >
            <ExecutionDiagnosisChat
              executionId={execId || ''}
              workflowId={workflowId || ''}
              workflowName={workflow.name || 'Workflow'}
              onClose={() => setDiagnosisChatVisible(false)}
            />
          </div>
          </>
        )}
      </div>
    </div>
  );
};

// Inline approval controls rendered in the step-detail sidebar so reviewers can
// resolve a gate (Approve / Reject, plus Request changes for refine gates)
// without navigating back to the Pending Approvals page. PR/Slack gates resolve
// out-of-band, so we surface their external link instead of Approve/Reject.
function StepApprovalActions({
  approval,
  feedback,
  onFeedbackChange,
  isProcessing,
  message,
  onApprove,
  onReject,
  onRequestChanges,
}: {
  approval: WorkflowApproval;
  feedback: string;
  onFeedbackChange: (val: string) => void;
  isProcessing: boolean;
  message: { type: 'success' | 'error'; text: string } | null;
  onApprove: () => void;
  onReject: () => void;
  onRequestChanges: () => void;
}) {
  const ctx = (approval.context || {}) as Record<string, any>;
  const isPrOrSlack = approval.approval_type === 'pr_approval' || approval.approval_type === 'pr_merge';
  const isRefine = ctx.refine_round !== undefined || ctx.max_rounds !== undefined;
  const round = typeof ctx.refine_round === 'number' ? ctx.refine_round : 0;
  const maxRounds = typeof ctx.max_rounds === 'number' ? ctx.max_rounds : undefined;

  return (
    <div className="border border-border bg-muted/30 rounded-md p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">Approval required</span>
        {isRefine && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            Refine{maxRounds ? ` ${round + 1}/${maxRounds}` : ''}
          </Badge>
        )}
      </div>

      {isPrOrSlack ? (
        approval.pr_url ? (
          <a
            href={approval.pr_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-background hover:bg-muted transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            {approval.pr_url.includes('gitlab') ? 'Review MR' : 'Review PR'}
          </a>
        ) : (
          <p className="text-xs text-muted-foreground">
            This gate is resolved via {approval.approval_type === 'slack' ? 'Slack' : 'the linked PR'}.
          </p>
        )
      ) : (
        <>
          {isRefine && (
            <div>
              <label className="block text-[11px] text-muted-foreground mb-1">
                Feedback / guidance for the AI (used when you Approve or Request changes)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => onFeedbackChange(e.target.value)}
                disabled={isProcessing}
                rows={3}
                placeholder="e.g. The root cause is wrong — the OOMKill is caused by the sidecar, not the app. Bump the sidecar memory limit instead."
                className="w-full text-xs rounded-md border border-border bg-background px-2 py-1.5 resize-y focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={onApprove} disabled={isProcessing} className="gap-1 h-7 text-xs">
              {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
            </Button>
            {isRefine && (
              <Button variant="outline" size="sm" onClick={onRequestChanges} disabled={isProcessing} className="gap-1 h-7 text-xs">
                <RotateCcw className="w-3 h-3" /> Request changes
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onReject} disabled={isProcessing} className="gap-1 h-7 text-xs">
              <XCircle className="w-3 h-3" /> Reject
            </Button>
          </div>
        </>
      )}

      {message && (
        <p className={`text-xs ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}

function getExternalIcon(integration: string, isDark: boolean): React.ReactNode {
  switch (integration) {
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-3.5 h-3.5" />;
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" className="w-3.5 h-3.5" />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" className="w-3.5 h-3.5" />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" className="w-3.5 h-3.5" />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" className="w-3.5 h-3.5" />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className="w-3.5 h-3.5" />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" className="w-3.5 h-3.5" />;
    default:
      return <ExternalLink className="w-3.5 h-3.5" />;
  }
}

function getExternalLabel(integration: string): string {
  const labels: Record<string, string> = {
    github: 'GitHub', gitlab: 'GitLab', slack: 'Slack',
    pagerduty: 'PagerDuty', jira: 'Jira', linear: 'Linear', confluence: 'Confluence',
  };
  return labels[integration] || integration;
}

function extractExternalLinks(step: any): { url: string; integration: string; label: string }[] {
  const links: { url: string; integration: string; label: string }[] = [];
  if (!step?.output) return links;
  const out = step.output;

  if (out.pr_url) {
    const isGitLab = out.pr_url.includes('gitlab');
    links.push({ url: out.pr_url, integration: isGitLab ? 'gitlab' : 'github', label: isGitLab ? 'View Merge Request' : 'View Pull Request' });
  }
  if (out.mr_url) {
    links.push({ url: out.mr_url, integration: 'gitlab', label: 'View Merge Request' });
  }
  if (out.issue_url) {
    // Disambiguate by integration / URL host
    if (step.integration === 'linear' || (out.issue_url as string).includes('linear.app')) {
      links.push({ url: out.issue_url, integration: 'linear', label: 'View Issue' });
    } else if (step.integration === 'posthog' || (out.issue_url as string).includes('error_tracking')) {
      links.push({ url: out.issue_url, integration: 'posthog', label: 'View Error Issue' });
    } else {
      const isGitLab = (out.issue_url as string).includes('gitlab');
      links.push({ url: out.issue_url, integration: isGitLab ? 'gitlab' : 'github', label: 'View Issue' });
    }
  }
  if (out.run_url) {
    links.push({ url: out.run_url, integration: 'github', label: 'View Workflow Run' });
  }
  if (out.pipeline_url) {
    links.push({ url: out.pipeline_url, integration: 'gitlab', label: 'View Pipeline' });
  }
  if (out.ticket_url) {
    links.push({ url: out.ticket_url, integration: 'jira', label: 'View Ticket' });
  }
  if (out.page_url) {
    links.push({ url: out.page_url, integration: 'confluence', label: 'View Page' });
  }
  if (out.pagerduty_url) {
    links.push({ url: out.pagerduty_url, integration: 'pagerduty', label: 'View Incident' });
  }
  if (out.permalink || out.message_url) {
    const url = out.permalink || out.message_url;
    links.push({ url, integration: 'slack', label: 'View in Slack' });
  }
  if (out.inspector_url) {
    links.push({ url: out.inspector_url, integration: 'vercel', label: 'View in Vercel' });
  }
  // PostHog: prefer specific URL fields; replay_url exists on session summary/get-recording already handled
  if (out.replay_url && !out.summaries) {
    links.push({ url: out.replay_url, integration: 'posthog', label: 'View Session Replay' });
  }

  // Approval gate: extract PR URL from the approval context
  if (step.approval_type === 'pr_approval' || step.approval_type === 'pr_merge') {
    if (step.pr_url) {
      const isGL = step.pr_url.includes('gitlab');
      links.push({ url: step.pr_url, integration: isGL ? 'gitlab' : 'github', label: isGL ? 'View Merge Request' : 'View Pull Request' });
    }
  }

  return links;
}

function ExternalStepLinks({ step }: { step: any }) {
  const isDark = useIsDark();
  const links = extractExternalLinks(step);
  if (links.length === 0) return null;

  return (
    <div className="space-y-1.5 mt-1">
      {links.map((link, i) => (
        <a
          key={i}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-primary hover:underline"
        >
          {getExternalIcon(link.integration, isDark)}
          <span>{link.label}</span>
          <ExternalLink className="w-3 h-3 opacity-50" />
        </a>
      ))}
    </div>
  );
}

function DatadogMetricsFromStep({ output }: { output: any }) {
  const { metrics, queryStr } = React.useMemo(() => {
    try {
      const raw = typeof output.response_data === 'string'
        ? JSON.parse(output.response_data)
        : output.data;
      if (!raw) return { metrics: null, queryStr: '' };

      const q = raw.query || output.query || '';

      if (raw.series && Array.isArray(raw.series) && raw.series.length > 0) {
        const hasData = raw.series.some((s: any) => s.pointlist && s.pointlist.length > 0);
        if (hasData) {
          return { metrics: [{ query: q, series: raw.series }], queryStr: q };
        }
      }
      return { metrics: null, queryStr: q };
    } catch {
      return { metrics: null, queryStr: '' };
    }
  }, [output]);

  return (
    <div className="space-y-2">
      <span className="font-medium text-sm">Metrics</span>
      {queryStr && (
        <div>
          <span className="text-muted-foreground text-xs">Query:</span>
          <code className="block text-xs bg-muted p-1.5 rounded mt-0.5 break-all">{queryStr}</code>
        </div>
      )}
      {metrics ? (
        <DatadogMetricsChart metrics={metrics} />
      ) : (
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded text-center">
          No metric data points returned for this query and time range.
        </div>
      )}
    </div>
  );
}

function ExecutionStatusBadge({ status }: { status: string }) {
  const config: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    running:          { variant: 'default', label: 'Running' },
    completed:        { variant: 'secondary', label: 'Completed' },
    failed:           { variant: 'destructive', label: 'Failed' },
    cancelled:        { variant: 'outline', label: 'Cancelled' },
    waiting_approval: { variant: 'default', label: 'Awaiting Approval' },
    waiting:          { variant: 'default', label: 'Waiting' },
  };
  const c = config[status] || config.running;
  return <Badge variant={c.variant}>{c.label}</Badge>;
}

function FixesFromRCAStep({ stepResults }: { stepResults?: any[] }) {
  if (!stepResults || !Array.isArray(stepResults)) return null;
  const rcaStep = stepResults.find((sr: any) => sr.action === 'kestrel-trigger-rca');
  if (!rcaStep?.output?.fixes || !Array.isArray(rcaStep.output.fixes)) {
    return <div className="text-xs text-muted-foreground">No fixes generated (fixes come from the Trigger RCA step).</div>;
  }
  const fixes = rcaStep.output.fixes;
  return (
    <div>
      <span className="font-medium">{fixes.length} Fix(es) from RCA</span>
      <div className="mt-1 space-y-3">
        {fixes.map((fix: any, i: number) => (
          <YAMLFixCard key={i} fix={fix} />
        ))}
      </div>
    </div>
  );
}

function YAMLFixCard({ fix }: { fix: any }) {
  const [showDiff, setShowDiff] = React.useState(true);
  const hasYAML = fix.yaml_content || fix.existing_yaml;
  const hasCLI = fix.cli_command;
  const hasTerraform = fix.type === 'terraform' && fix.content;

  // Older executions stored `resource` via Go's fmt with nil values, yielding
  // "%!s(<nil>)/%!s(<nil>)". Ignore that sentinel and fall back to other fields.
  const cleanResource =
    typeof fix.resource === 'string' && !fix.resource.includes('%!s(<nil>)') ? fix.resource : '';
  const resourceLabel =
    cleanResource ||
    fix.file_path ||
    [fix.resource_namespace, fix.resource_name].filter(Boolean).join('/') ||
    fix.resource_name ||
    fix.service ||
    'Fix';

  return (
    <div className="text-xs border rounded-md overflow-hidden">
      <div className="p-2 bg-muted/50 border-b">
        <div className="font-medium">{resourceLabel} <span className="text-muted-foreground">({fix.type}{fix.operation ? `: ${fix.operation}` : ''})</span></div>
        <div className="mt-0.5 leading-relaxed text-muted-foreground">{parseFormattedText(fix.description || fix.explanation || '')}</div>
      </div>
      {hasCLI && (
        <div className="font-mono text-xs max-h-64 overflow-auto">
          <pre className="p-2 whitespace-pre-wrap bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100">{fix.cli_command}</pre>
        </div>
      )}
      {hasTerraform && (
        <div className="font-mono text-xs max-h-64 overflow-auto">
          <pre className="p-2 whitespace-pre-wrap bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100">{fix.content}</pre>
        </div>
      )}
      {hasYAML && (
        <>
          <div className="flex items-center gap-1 px-2 py-1 border-b bg-muted/30">
            {fix.existing_yaml && fix.yaml_content && (
              <>
                <button
                  onClick={() => setShowDiff(true)}
                  className={`px-2 py-0.5 rounded text-[10px] ${showDiff ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                >
                  Diff
                </button>
                <button
                  onClick={() => setShowDiff(false)}
                  className={`px-2 py-0.5 rounded text-[10px] ${!showDiff ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                >
                  Full YAML
                </button>
              </>
            )}
          </div>
          <div className="font-mono text-xs max-h-64 overflow-auto">
            {showDiff && fix.existing_yaml && fix.yaml_content ? (
              <DiffView oldText={fix.existing_yaml} newText={fix.yaml_content} gutterWidth="w-6" />
            ) : (
              <pre className="p-2 whitespace-pre-wrap">{fix.yaml_content || fix.existing_yaml}</pre>
            )}
          </div>
        </>
      )}
      {!hasYAML && !hasCLI && !hasTerraform && fix.rollback_command && (
        <div className="font-mono text-xs max-h-64 overflow-auto">
          <pre className="p-2 whitespace-pre-wrap bg-muted">{fix.rollback_command}</pre>
        </div>
      )}
    </div>
  );
}

function ReplayDropdown({ loading, onReplay }: { loading: boolean; onReplay: (mode: 'full' | 'from_failed') => void }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        className="gap-1"
        disabled={loading}
        onClick={() => setOpen(!open)}
      >
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        Replay
        <ChevronDown className="w-3 h-3 ml-0.5" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-52 rounded-md border bg-popover shadow-md py-1">
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
            onClick={() => { onReplay('from_failed'); setOpen(false); }}
          >
            Replay from failed step
          </button>
          <button
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
            onClick={() => { onReplay('full'); setOpen(false); }}
          >
            Replay from beginning
          </button>
        </div>
      )}
    </div>
  );
}

export default WorkflowExecutionDetail;
